import { HttpClient, HttpParams } from '@angular/common/http';
import memoryStorage from './storage/MemoryStorage';
import { Observable, of } from 'rxjs';
import { tap, share, switchMap } from 'rxjs/operators';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';

export type StorageType = 'local' | 'session' | 'memory' | null;

type StorageExtended = Storage | null;

interface StorageOptions {
  storageType?: StorageType;
  ttlMS?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  baseUrl = this.isBrowserOnly ? '' : environment.apiUrl;

  get isBrowserOnly(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public get<T>(
    url: string,
    options?: { params: HttpParams },
    storageOptions: StorageOptions = {}
  ): Observable<T> {
    return this.request<T>('GET', url, options, storageOptions);
  }

  public post<T>(
    url: string,
    body: any,
    options?: { params: HttpParams },
    storageOptions: StorageOptions = {}
  ): Observable<T> {
    return this.request<T>('POST', url, { body, ...options }, storageOptions);
  }

  private request<T>(
    method: 'GET' | 'POST',
    url: string,
    options?: { params: HttpParams; body?: any },
    storageOptions: StorageOptions = {}
  ): Observable<T> {
    const { storageType, ttlMS } = storageOptions;
    const storage = this.useStorage(storageType);

    const fromStorage$ = storage
      ? this.getStorageItem(url, options?.params, storage)
      : of(null);

    return fromStorage$.pipe(
      switchMap((cachedResponse) => {
        if (cachedResponse) {
          return of(cachedResponse);
        }

        const response$ = this.http
          .request<T>(method, url, options)
          .pipe(share());

        if (storage && this.isBrowserOnly) {
          return response$.pipe(
            tap((response) =>
              this.setStorageItem(
                url,
                options?.params,
                response,
                storage,
                ttlMS,
                storageType
              )
            )
          );
        }

        return response$;
      })
    );
  }

  public clearStorage() {
    // @todo add more fine grain control
    localStorage.clear();
    sessionStorage.clear();
    memoryStorage.clear();
    // the backend will clear cache on any listings or settings updates
    // return this.get('/api/flushServerCache');
    return;
  }

  private useStorage(storageType?: StorageType) {
    let storage: StorageExtended = null;
    switch (storageType) {
      case 'local':
        storage = localStorage;
        break;
      case 'session':
        storage = sessionStorage;
        break;
      case 'memory':
        storage = memoryStorage;
        break;
      default:
        storage = null;
    }
    return storage;
  }

  private createCacheKey(url: string, params?: HttpParams): string {
    const paramsString = params ? params.toString() : '';
    const key = url + paramsString;
    return btoa(key).replace(/=/g, ''); // Encode to base64 to ensure it's a valid key and remove padding
  }

  private getStorageItem(
    url: string,
    params?: HttpParams,
    storage?: Storage
  ): Observable<any | null> {
    const key = this.createCacheKey(url, params);
    const cachedResponse = storage.getItem(key);
    if (cachedResponse) {
      const cachedResponseObj = JSON.parse(cachedResponse);
      const now = new Date().getTime();
      if (cachedResponseObj.time && cachedResponseObj.time - now > 0) {
        // console.log(`Cache hit: ${key}`);
        return of(cachedResponseObj.data);
      }
    }
    return of(null);
  }

  private setStorageItem(
    url: string,
    params: HttpParams,
    response: any,
    storage: Storage,
    ttlInMS = 120000,
    storageType: StorageType = null
  ): void {
    const key = this.createCacheKey(url, params);
    if (response) {
      console.log(`Cache set: ${key} ${storageType}`);
      const cacheResponse = {
        data: response,
        time: new Date().getTime() + ttlInMS,
      };
      storage.setItem(key, JSON.stringify(cacheResponse));
    }
  }
}
